import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { SubmissionsList } from "./SubmissionsList";
import { toast } from "sonner";

interface MealTrackingDashboardProps {
  planId: string;
  totalDailyCalories: number;
}

interface MealSubmission {
  id: string;
  meal_item_id: string | null;
  scheduled_time: string | null;
  actual_time: string | null;
  photo_path: string;
  ai_calculated_calories: number | null;
  ai_detected_foods: any;
  status: string | null;
  submitted_at: string | null;
  day_number: number | null;
  meal_item?: {
    meal_name: string;
    meal_type: string;
    total_calories: number | null;
    day_number: number | null;
  };
}

const dayNames = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

export function MealTrackingDashboard({ planId, totalDailyCalories }: MealTrackingDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState<MealSubmission[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);

  useEffect(() => {
    loadSubmissions();

    // Realtime subscription
    const channel = supabase
      .channel(`meal-tracking-${planId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'meal_submissions',
          filter: `link_id=eq.${planId}`
        },
        () => {
          loadSubmissions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [planId]);

  const loadSubmissions = async () => {
    try {
      // Get tracking link for this plan
      const { data: plan } = await supabase
        .from("meal_plans")
        .select("tracking_link_id")
        .eq("id", planId)
        .single();

      if (!plan?.tracking_link_id) {
        setLoading(false);
        return;
      }

      // Get tracking link
      const { data: trackingLink } = await supabase
        .from("meal_tracking_links")
        .select("id")
        .eq("id", plan.tracking_link_id)
        .single();

      if (!trackingLink) {
        setLoading(false);
        return;
      }

      // Get submissions
      const { data: submissionsData, error } = await supabase
        .from("meal_submissions")
        .select(`
          *,
          meal_item:meal_items(meal_name, meal_type, total_calories, day_number)
        `)
        .eq("link_id", trackingLink.id)
        .order("submitted_at", { ascending: false });

      if (error) throw error;

      setSubmissions(submissionsData || []);

      // Process chart data
      const dataByDay: Record<number, { predicted: number; actual: number }> = {};

      // Initialize all 7 days
      for (let i = 1; i <= 7; i++) {
        dataByDay[i] = { predicted: totalDailyCalories, actual: 0 };
      }

      // Aggregate actual calories by day
      submissionsData?.forEach((sub) => {
        const dayNum = sub.day_number || 1;
        if (!dataByDay[dayNum]) {
          dataByDay[dayNum] = { predicted: totalDailyCalories, actual: 0 };
        }
        dataByDay[dayNum].actual += sub.ai_calculated_calories || 0;
      });

      const chart = Object.entries(dataByDay).map(([day, data]) => ({
        day: dayNames[parseInt(day) - 1],
        Previsto: data.predicted,
        Realizado: data.actual,
      }));

      setChartData(chart);
    } catch (error) {
      console.error("Error loading submissions:", error);
      toast.error("Erro ao carregar acompanhamento");
    } finally {
      setLoading(false);
    }
  };

  const totalMeals = submissions.length;
  const avgActualCalories = totalMeals > 0
    ? submissions.reduce((sum, s) => sum + (s.ai_calculated_calories || 0), 0) / Math.max(submissions.filter(s => s.day_number).map(s => s.day_number!).filter((v, i, a) => a.indexOf(v) === i).length, 1)
    : 0;
  const adherence = totalMeals > 0 ? Math.round((totalMeals / (7 * 4)) * 100) : 0; // Assuming 4 meals/day

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Média Prevista
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{totalDailyCalories} <span className="text-lg text-muted-foreground">kcal</span></p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Média Realizada
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{Math.round(avgActualCalories)} <span className="text-lg text-muted-foreground">kcal</span></p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Aderência
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{adherence}% <span className="text-sm text-muted-foreground">({totalMeals} refeições)</span></p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle>📊 Previsto vs Realizado por Dia</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Previsto" fill="hsl(var(--primary))" />
              <Bar dataKey="Realizado" fill="hsl(var(--secondary))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Submissions List */}
      <Card>
        <CardHeader>
          <CardTitle>📝 Registros Recentes</CardTitle>
        </CardHeader>
        <CardContent>
          <SubmissionsList submissions={submissions} />
        </CardContent>
      </Card>
    </div>
  );
}
